import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ArrowLeft,
  Calendar,
  Play,
  Pause,
  Clock,
  Speedometer,
  Path,
  MapPin,
  CaretLeft,
  CaretRight,
  Export,
  Circle,
} from '@phosphor-icons/react';
import { tracksAPI, devicesAPI } from '@/services/api';
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';

// Custom start/end markers
const startIcon = L.divIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 24px;
    height: 24px;
    background: #10b981;
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const endIcon = L.divIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 24px;
    height: 24px;
    background: #ef4444;
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const movingIcon = L.divIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 20px;
    height: 20px;
    background: #3b82f6;
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Fit bounds component
const FitBounds = ({ bounds }) => {
  const map = useMap();
  
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  
  return null;
};

const Tracks = () => {
  const { imei } = useParams();
  const navigate = useNavigate();
  
  const [device, setDevice] = useState(null);
  const [trackData, setTrackData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 1), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackIndex, setPlaybackIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [mapType, setMapType] = useState('hybrid'); // 'osm', 'satellite', 'hybrid'
  
  // Map tile configurations
  const mapTiles = {
    osm: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    },
    satellite: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      attribution: '&copy; Esri'
    },
    hybrid: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      attribution: '&copy; Esri',
      labelsUrl: 'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}'
    }
  };

  // Fetch device info
  useEffect(() => {
    const fetchDevice = async () => {
      try {
        const response = await devicesAPI.getDetail(imei);
        setDevice(response.data);
      } catch (err) {
        console.error('Error fetching device:', err);
      }
    };
    
    if (imei) fetchDevice();
  }, [imei]);

  // Fetch track data
  const fetchTrack = async () => {
    setLoading(true);
    try {
      const beginTime = `${startDate} ${startTime}:00`;
      const endTimeStr = `${endDate} ${endTime}:59`;
      
      const response = await tracksAPI.getTrack(imei, beginTime, endTimeStr);
      const data = response.data || [];
      
      // Format track points
      const points = data.map(point => ({
        lat: parseFloat(point.lat),
        lng: parseFloat(point.lng),
        speed: point.speed,
        time: point.gpsTime,
        address: point.address,
      })).filter(p => p.lat && p.lng);
      
      setTrackData(points);
      setPlaybackIndex(0);
    } catch (err) {
      console.error('Error fetching track:', err);
    } finally {
      setLoading(false);
    }
  };

  // Playback animation
  useEffect(() => {
    let interval;
    
    if (isPlaying && trackData.length > 0) {
      interval = setInterval(() => {
        setPlaybackIndex(prev => {
          if (prev >= trackData.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1000 / playbackSpeed);
    }
    
    return () => clearInterval(interval);
  }, [isPlaying, trackData.length, playbackSpeed]);

  // Get polyline positions
  const positions = trackData.map(p => [p.lat, p.lng]);
  const currentPosition = trackData[playbackIndex];
  
  // Calculate stats
  const totalPoints = trackData.length;
  const maxSpeed = trackData.reduce((max, p) => Math.max(max, p.speed || 0), 0);
  const avgSpeed = trackData.length > 0 
    ? trackData.reduce((sum, p) => sum + (p.speed || 0), 0) / trackData.length 
    : 0;

  return (
    <div className="h-screen w-screen flex flex-col bg-gray-100" data-testid="tracks-page">
      {/* Header */}
      <header className="h-16 border-b border-gray-200 flex items-center justify-between px-4 bg-white/95 backdrop-blur">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-bold flex items-center gap-2">
              <Path size={20} className="text-primary" />
              Historial de Recorridos
            </h1>
            <p className="text-xs text-gray-600">
              {device?.deviceName || imei}
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Sidebar - Controls */}
        <aside className="w-80 border-r border-gray-200 bg-white/95 p-4 space-y-4 overflow-y-auto">
          {/* Date Range */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Calendar size={16} /> Rango de Fechas
            </h3>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600 font-medium">Fecha inicio</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="start-date-input"
                  className="w-full h-9 px-2 rounded-lg bg-white border border-gray-300 text-sm text-gray-800"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium">Hora inicio</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  data-testid="start-time-input"
                  className="w-full h-9 px-2 rounded-lg bg-white border border-gray-300 text-sm text-gray-800"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium">Fecha fin</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="end-date-input"
                  className="w-full h-9 px-2 rounded-lg bg-white border border-gray-300 text-sm text-gray-800"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 font-medium">Hora fin</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  data-testid="end-time-input"
                  className="w-full h-9 px-2 rounded-lg bg-white border border-gray-300 text-sm text-gray-800"
                />
              </div>
            </div>

            <button
              onClick={fetchTrack}
              disabled={loading}
              data-testid="fetch-track-btn"
              className="w-full h-10 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <Circle size={18} className="spinner" />
              ) : (
                <>
                  <Path size={18} /> Cargar Recorrido
                </>
              )}
            </button>
          </div>

          {/* Stats */}
          {trackData.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Estadísticas</h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Puntos</div>
                  <div className="font-bold">{totalPoints}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Vel. Máx</div>
                  <div className="font-bold">{Math.round(maxSpeed)} km/h</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Vel. Prom</div>
                  <div className="font-bold">{Math.round(avgSpeed)} km/h</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Tiempo</div>
                  <div className="font-bold text-xs">
                    {trackData.length > 0 && trackData[0].time ? 
                      format(new Date(trackData[0].time), 'HH:mm', { locale: es }) : '-'}
                    {' - '}
                    {trackData.length > 0 && trackData[trackData.length - 1].time ?
                      format(new Date(trackData[trackData.length - 1].time), 'HH:mm', { locale: es }) : '-'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Playback Controls */}
          {trackData.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Reproducción</h3>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPlaybackIndex(Math.max(0, playbackIndex - 1))}
                  className="p-2 rounded-lg bg-gray-200 hover:bg-gray-200/80 transition-colors"
                  data-testid="prev-point-btn"
                >
                  <CaretLeft size={18} />
                </button>
                
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="flex-1 h-10 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                  data-testid="play-pause-btn"
                >
                  {isPlaying ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
                  {isPlaying ? 'Pausar' : 'Reproducir'}
                </button>
                
                <button
                  onClick={() => setPlaybackIndex(Math.min(trackData.length - 1, playbackIndex + 1))}
                  className="p-2 rounded-lg bg-gray-200 hover:bg-gray-200/80 transition-colors"
                  data-testid="next-point-btn"
                >
                  <CaretRight size={18} />
                </button>
              </div>

              {/* Speed control */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Velocidad:</span>
                {[1, 2, 5, 10].map(speed => (
                  <button
                    key={speed}
                    onClick={() => setPlaybackSpeed(speed)}
                    data-testid={`speed-${speed}x-btn`}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      playbackSpeed === speed 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-gray-200 text-gray-500 hover:bg-gray-200/80'
                    }`}
                  >
                    {speed}x
                  </button>
                ))}
              </div>

              {/* Progress bar */}
              <div className="space-y-1">
                <input
                  type="range"
                  min={0}
                  max={trackData.length - 1}
                  value={playbackIndex}
                  onChange={(e) => setPlaybackIndex(parseInt(e.target.value))}
                  className="w-full"
                  data-testid="playback-slider"
                />
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{playbackIndex + 1} / {trackData.length}</span>
                  {currentPosition && (
                    <span>{currentPosition.speed || 0} km/h</span>
                  )}
                </div>
              </div>

              {/* Current point info */}
              {currentPosition && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock size={14} />
                    <span>{currentPosition.time ? format(new Date(currentPosition.time), 'dd/MM HH:mm:ss', { locale: es }) : '-'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Speedometer size={14} />
                    <span>{currentPosition.speed || 0} km/h</span>
                  </div>
                  <div className="flex items-start gap-2 text-xs text-gray-600">
                    <MapPin size={14} className="mt-0.5" />
                    <span>{currentPosition.address || `${currentPosition.lat.toFixed(6)}, ${currentPosition.lng.toFixed(6)}`}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </aside>

        {/* Map */}
        <main className="flex-1 relative">
          <MapContainer
            center={[20, 0]}
            zoom={3}
            className="h-full w-full"
            zoomControl={false}
            data-testid="tracks-map"
          >
            <TileLayer
              attribution={mapTiles[mapType].attribution}
              url={mapTiles[mapType].url}
            />
            {mapType === 'hybrid' && (
              <TileLayer
                url="https://stamen-tiles-{s}.a.ssl.fastly.net/toner-labels/{z}/{x}/{y}{r}.png"
                opacity={0.7}
              />
            )}
            
            {/* Map type selector */}
            <div className="absolute top-3 right-3 z-[1000] bg-white rounded-lg shadow-lg p-1 flex gap-1">
              <button
                onClick={() => setMapType('osm')}
                className={`px-2 py-1 text-xs rounded ${mapType === 'osm' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Mapa
              </button>
              <button
                onClick={() => setMapType('satellite')}
                className={`px-2 py-1 text-xs rounded ${mapType === 'satellite' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Satélite
              </button>
              <button
                onClick={() => setMapType('hybrid')}
                className={`px-2 py-1 text-xs rounded ${mapType === 'hybrid' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Híbrido
              </button>
            </div>
            
            {positions.length > 0 && <FitBounds bounds={positions} />}
            
            {/* Track polyline */}
            {positions.length > 0 && (
              <Polyline
                positions={positions}
                pathOptions={{
                  color: '#3b82f6',
                  weight: 4,
                  opacity: 0.8,
                }}
              />
            )}
            
            {/* Start marker */}
            {trackData.length > 0 && (
              <Marker position={[trackData[0].lat, trackData[0].lng]} icon={startIcon}>
                <Popup>
                  <div className="text-xs">
                    <strong>Inicio</strong><br />
                    {trackData[0].time ? format(new Date(trackData[0].time), 'dd/MM/yyyy HH:mm', { locale: es }) : '-'}
                  </div>
                </Popup>
              </Marker>
            )}
            
            {/* End marker */}
            {trackData.length > 1 && (
              <Marker 
                position={[trackData[trackData.length - 1].lat, trackData[trackData.length - 1].lng]} 
                icon={endIcon}
              >
                <Popup>
                  <div className="text-xs">
                    <strong>Fin</strong><br />
                    {trackData[trackData.length - 1].time ? 
                      format(new Date(trackData[trackData.length - 1].time), 'dd/MM/yyyy HH:mm', { locale: es }) : '-'}
                  </div>
                </Popup>
              </Marker>
            )}
            
            {/* Current playback position */}
            {currentPosition && playbackIndex > 0 && playbackIndex < trackData.length - 1 && (
              <Marker 
                position={[currentPosition.lat, currentPosition.lng]} 
                icon={movingIcon}
              >
                <Popup>
                  <div className="text-xs">
                    <strong>Posición actual</strong><br />
                    {currentPosition.speed || 0} km/h<br />
                    {currentPosition.time ? format(new Date(currentPosition.time), 'HH:mm:ss', { locale: es }) : '-'}
                  </div>
                </Popup>
              </Marker>
            )}
          </MapContainer>

          {/* No data message */}
          {!loading && trackData.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80">
              <div className="text-center">
                <Path size={48} className="mx-auto mb-4 text-gray-500" />
                <h3 className="font-semibold mb-2">Sin datos de recorrido</h3>
                <p className="text-sm text-gray-500">
                  Selecciona un rango de fechas y haz clic en "Cargar Recorrido"
                </p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Tracks;
